import { useState } from 'react';
import { IconRight } from '@ui/src';
import { useAppSelector } from '@store';
import BrandColorExampleAvatar from './images/header-avatar';
import hexToRGBAndHue from './format-hex-to-rgb';
import brandColorExampleAccounts from './images/brand-color-example-accounts.png';
import IconTopUp from './images/top-up';
import IconPay from './images/pay';
import IconExchange from './images/exchange';
import IconBetweenAccounts from './images/between-accounts';
import IconCashCollection from './images/cash-collection';
import { BrandColorExampleTypes } from './types';
import { PSPDetailsKeys } from '../../../hoc/settings/types';

import cn from './styles.module.css';

const BrandColorsExample = ({ color, headerLogo }: BrandColorExampleTypes) => {
  const [linkHovered, setLinkHovered] = useState('');
  const [hoveredPayment, setHoveredPayment] = useState('');

  const { rgb } = hexToRGBAndHue(color);

  const { pspDetails } = useAppSelector((state) => state.settings.details);

  const topUpButtonName = pspDetails?.find(
    (item) => item.key === PSPDetailsKeys.TOP_UP,
  )?.data?.value;

  return (
    <div className={cn.brandColorsExampleView}>
      <div
        className={cn.brandColorsExampleViewHeaderBackground}
        style={{
          background: `linear-gradient(180deg, rgba(${rgb}, .1) 0, rgba(${rgb}, 0) 100%)`,
        }}
      />
      <div className={cn.brandColorsExampleViewHeader}>
        <div className={cn.brandColorsExampleViewHeaderContent}>
          <div className={cn.brandColorsExampleViewHeaderLogo}>
            <img
              src={headerLogo}
              alt="Header Logo"
              className={cn.brandColorsExampleViewHeaderLogoImage}
            />
          </div>
          <div className={cn.brandColorsExampleViewHeaderLinks}>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                borderBottom: `2px solid ${color}`,
                color,
              }}
            >
              Home
            </div>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                color: linkHovered === 'payments' ? color : '#171819',
              }}
              onMouseEnter={() => setLinkHovered('payments')}
              onMouseLeave={() => setLinkHovered('')}
            >
              Payments
            </div>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                color: linkHovered === 'beneficiaries' ? color : '#171819',
              }}
              onMouseEnter={() => setLinkHovered('beneficiaries')}
              onMouseLeave={() => setLinkHovered('')}
            >
              Beneficiaries
            </div>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                color: linkHovered === 'currency-exchange' ? color : '#171819',
              }}
              onMouseEnter={() => setLinkHovered('currency-exchange')}
              onMouseLeave={() => setLinkHovered('')}
            >
              Currency Exchange
            </div>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                color: linkHovered === 'history' ? color : '#171819',
              }}
              onMouseEnter={() => setLinkHovered('history')}
              onMouseLeave={() => setLinkHovered('')}
            >
              History
            </div>
            <div
              className={cn.brandColorsExampleViewHeaderLink}
              style={{
                color: linkHovered === 'statement' ? color : '#171819',
              }}
              onMouseEnter={() => setLinkHovered('statement')}
              onMouseLeave={() => setLinkHovered('')}
            >
              Statement
            </div>
          </div>
          <div className={cn.brandColorsExampleViewHeaderControls}>
            <div className={cn.brandColorsExampleViewHeaderName}>John Doe</div>
            <div
              className={cn.brandColorsExampleViewHeaderAvatar}
              style={{
                color,
              }}
            >
              <BrandColorExampleAvatar />
            </div>
          </div>
        </div>
      </div>
      <div className={cn.brandColorsExampleViewContent}>
        <div className={cn.brandColorsExampleViewContentLeft}>
          <img
            className={cn.brandColorsExampleViewAccounts}
            src={brandColorExampleAccounts}
            alt="Accounts"
          />
        </div>
        <div className={cn.brandColorsExampleViewContentRight}>
          <div className={cn.brandColorsExampleViewPayments}>
            <div
              className={cn.brandColorsExampleViewPaymentItem}
              onMouseEnter={() => setHoveredPayment('internal')}
              onMouseLeave={() => setHoveredPayment('')}
              style={{
                color: hoveredPayment === 'internal' ? color : '#171819',
              }}
            >
              <div
                style={{
                  background: color,
                }}
                className={cn.brandColorsExampleViewPaymentIcon}
              >
                <IconPay />
              </div>
              <span>Pay out</span>
            </div>
            <div
              className={cn.brandColorsExampleViewPaymentItem}
              onMouseEnter={() => setHoveredPayment('euro')}
              onMouseLeave={() => setHoveredPayment('')}
              style={{
                color: hoveredPayment === 'euro' ? color : '#171819',
              }}
            >
              <div
                style={{
                  background: color,
                }}
                className={cn.brandColorsExampleViewPaymentIcon}
              >
                <IconTopUp />
              </div>
              <span>{(topUpButtonName as string) || 'Top Up'}</span>
            </div>
            <div
              className={cn.brandColorsExampleViewPaymentItem}
              onMouseEnter={() => setHoveredPayment('gbp')}
              onMouseLeave={() => setHoveredPayment('')}
              style={{
                color: hoveredPayment === 'gbp' ? color : '#171819',
              }}
            >
              <div
                style={{
                  background: color,
                }}
                className={cn.brandColorsExampleViewPaymentIcon}
              >
                <IconExchange />
              </div>
              <span>Exchange</span>
            </div>
            <div
              className={cn.brandColorsExampleViewPaymentItem}
              onMouseEnter={() => setHoveredPayment('exchange')}
              onMouseLeave={() => setHoveredPayment('')}
              style={{
                color: hoveredPayment === 'exchange' ? color : '#171819',
              }}
            >
              <div
                style={{
                  background: color,
                }}
                className={cn.brandColorsExampleViewPaymentIcon}
              >
                <IconBetweenAccounts />
              </div>
              <span>Between accounts</span>
            </div>
            <div
              className={cn.brandColorsExampleViewPaymentItem}
              onMouseEnter={() => setHoveredPayment('international')}
              onMouseLeave={() => setHoveredPayment('')}
              style={{
                color: hoveredPayment === 'international' ? color : '#171819',
              }}
            >
              <div
                style={{
                  background: color,
                }}
                className={cn.brandColorsExampleViewPaymentIcon}
              >
                <IconCashCollection />
              </div>
              <span>Cash Collection</span>
            </div>
          </div>
          <div className={cn.brandColorsExampleViewBeneficiaries}>
            <div className={cn.brandColorsExampleViewBeneficiariesTitle}>
              Recent Beneficiaries
              <IconRight />
            </div>
            <div className={cn.brandColorsExampleViewBeneficiariesContent}>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  John Doe
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  12321312311
                </div>
              </div>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  John Doe
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  994039392909
                </div>
              </div>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  Scarlett Hansen
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  31243425457567
                </div>
              </div>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  Taylor Ruiz
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  12321312311
                </div>
              </div>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  Wren Carr
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  994039392909
                </div>
              </div>
              <div
                className={cn.brandColorsExampleViewBeneficiariesContentItem}
              >
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemName
                  }
                >
                  Wren Carr
                </div>
                <div
                  className={
                    cn.brandColorsExampleViewBeneficiariesContentItemAccount
                  }
                >
                  00029302
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandColorsExample;
